import SpotList from "../../components/spotList/SpotList";
import * as React from "react";
import {useCallback, useContext, useEffect, useRef} from "react";
import {CircleSpot, GpsShapeType, LatLong, MarkerSpot, overlayTypeToSpotType, PolygonSpot, Spot} from "../../components/types";
import {v4 as uuidv4} from 'uuid';
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {MapContext, MapContextI} from "../../components/context/MapContextWrapper";
import {DrawingMode, DrawResult, MarkerOverlay} from "../../components/map/drawingTypes";

type Props = {
    active: boolean
}

const Spots = ({active}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const mapContext: MapContextI = useContext<MapContextI>(MapContext);
    const canEdit = appContext.isDraft();

    const defaultRunOnes = !appContext.isOnDemand();
    const lastAppliedTriggerSelectionRef = useRef<string | undefined>(undefined);
    const lastDrawingConfigRef = useRef<string | undefined>(undefined);
    const appContextRef = useRef<AppContextI>(appContext);
    const mapContextRef = useRef<MapContextI>(mapContext);
    const onOverlayCompleteRef = useRef<(drawingResult: DrawResult) => void>(() => undefined);
    const spots = appContext.getSpots();
    const triggerSelection = mapContext.getTriggerSelection();

    useEffect(() => {
        appContextRef.current = appContext;
    }, [appContext]);

    useEffect(() => {
        mapContextRef.current = mapContext;
    }, [mapContext]);

    const getNextSpotName = useCallback(() => {
        const existingSpots = appContext.getSpots() || [];
        const highestNumber = existingSpots.reduce((max, spot) => {
            const match = /^(?:\d+\s*-\s*)?Spot\s+(\d+)$/.exec(spot.name || "");
            if (!match) {
                return max;
            }
            const num = parseInt(match[1], 10);
            return Number.isFinite(num) && num > max ? num : max;
        }, 0);
        const next = highestNumber + 1;
        return `${next} - Spot ${next}`;
    }, [appContext]);


    onOverlayCompleteRef.current = (drawingResult: DrawResult) => {
        const currentMapContext = mapContextRef.current;
        const currentAppContext = appContextRef.current;
        const currentTriggerSelection = currentMapContext.getTriggerSelection();
        if (currentTriggerSelection !== undefined) {

        } else if (drawingResult) {
            const drawingMode = drawingResult.type;
            const selectedSpot = currentAppContext.getSelection();

            const areaId = currentAppContext.getArea()?.id;
            let newSpot: Spot = {
                id: "",
                area: areaId ? areaId : "EMPTY-AREA",
                name: getNextSpotName(),
                type: overlayTypeToSpotType(drawingMode),
                priority: 2,
                beaconSensitivity: 8,
                pauseOtherAmbients: false,
                duckOtherAmbients: true,
                duckOtherAmbientsDb: 0,
                followMainSound: false,
                playMode: "play",
                playFullAfter: 0,
                loop: false,
                runOnes: defaultRunOnes,
                modifiedAt: 0,
                key: ""
            }

            switch (drawingMode) {
                case DrawingMode.CIRCLE:
                    const radius = (drawingResult.overlay as google.maps.Circle).getRadius();
                    const center = (drawingResult.overlay as google.maps.Circle).getCenter();
                    newSpot = {
                        ...newSpot,
                        center: center || {},
                        radius: radius || 20
                    } as CircleSpot
                    break;

                case DrawingMode.MARKER:
                    const markerOverlay = drawingResult.overlay as MarkerOverlay;
                    const markerPosition = markerOverlay.position;
                    const markerCenter = markerPosition instanceof google.maps.LatLng
                        ? {
                            lat: markerPosition.lat(),
                            lng: markerPosition.lng()
                        }
                        : markerPosition
                            ? {
                                lat: markerPosition.lat,
                                lng: markerPosition.lng
                            }
                            : undefined;
                    newSpot = {
                        ...newSpot,
                        position: markerCenter || {}
                    } as MarkerSpot
                    break;

                case DrawingMode.POLYGON:
                    const path = (drawingResult.overlay as google.maps.Polygon).getPath();
                    const array: LatLong[] = path.getArray().map((latLng: google.maps.LatLng) => {
                        return {
                            lat: latLng.lat(),
                            lng: latLng.lng()
                        }
                    });
                    newSpot = {
                        ...newSpot,
                        coords: array,
                        gpsTriggers: [{
                            id: uuidv4(),
                            type: "GPS",
                            shapeType: GpsShapeType.Polygon,
                            polygonCoords: array
                        }]
                    } as PolygonSpot
                    break;
                default:
                    null;
            }
            if (selectedSpot) {
                currentAppContext.setSelection(undefined);
            }

            const spotCallback = currentAppContext.createSpot(newSpot);
            spotCallback && spotCallback.then(() => {
                const overlay = drawingResult.overlay as {
                    setMap?: (map: google.maps.Map | null) => void
                    map?: google.maps.Map | null
                };
                if (typeof overlay.setMap === "function") {
                    overlay.setMap(null);
                } else if ("map" in overlay) {
                    overlay.map = null;
                }
            })
        }
    };


    useEffect(() => {
        if (!active) {
            return;
        }
        const nextConfigKey = `${active}|${canEdit}`;
        if (lastDrawingConfigRef.current === nextConfigKey) {
            return;
        }
        lastDrawingConfigRef.current = nextConfigKey;
        mapContextRef.current.setDrawingModes(canEdit ? [DrawingMode.MARKER, DrawingMode.POLYGON] : []);
        mapContextRef.current.setOnOverlayComplete((drawingResult: DrawResult) => onOverlayCompleteRef.current(drawingResult));
    }, [active, canEdit]);

    useEffect(() => {
        if (triggerSelection === lastAppliedTriggerSelectionRef.current) {
            return;
        }
        lastAppliedTriggerSelectionRef.current = triggerSelection;
        if (!triggerSelection) {
            return;
        }
        const spot = spots.find((currentSpot) => currentSpot.id === triggerSelection);
        if (spot && appContextRef.current.getSelection()?.id !== spot.id) {
            appContextRef.current.setSelection(spot);
        }
    }, [spots, triggerSelection]);


    return <SpotList/>;
}

export default Spots;
